import { useState, type FormEvent } from 'react';
import { Badge, Button, Card, CardHeader, Input, Modal, ModalFooter, getStatusBadgeVariant } from '@/components/common';
import {
  useConfirmRenewalPayment,
  useCreateRenewalPaymentLink,
  useRejectRenewalPaymentReport,
  useRevokeRenewalPaymentLink,
} from '@/hooks/useRenewals';
import { useCan } from '@/hooks/useEmployeeRights';
import { renewalService } from '@/services/renewals';
import { formatCurrency, formatDateTime } from '@/utils';
import type { RenewalQueueDetail } from '@/types/renewalQueue';
import { renewalPaymentStateLabel } from '@/pages/policies/renewalPaymentState';

/** `<input type="datetime-local">` value for `date` in the browser's own zone. */
function localInputValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(
    date.getMinutes()
  )}`;
}

function toIso(local: string): string | null {
  if (!local) return null;
  const parsed = new Date(local);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

const RECEIPT_ACCEPT = 'application/pdf,image/jpeg,image/png';
const RECEIPT_MAX_BYTES = 5 * 1024 * 1024;

/**
 * #345 -- the insurer's renewal payment link on the broker renewal desk.
 *
 * The insurer generates the link on its own payment gateway; the desk pastes
 * it here, the customer pays through "Renew now", and the desk confirms the
 * payment (with the insurer's receipt when it has one). The list payload never
 * carries the URL: "Show link" asks the server for it (audited).
 */
export function RenewalPaymentLinkCard({ item, routeId }: { item: RenewalQueueDetail; routeId?: string }) {
  const canEdit = useCan('renewals', 'edit');
  const canConfirm = useCan('policies', 'edit') && canEdit;
  const block = item.renewal_payment;
  const state = block?.state ?? 'no_link';
  const label = renewalPaymentStateLabel(state);
  const ids = { policyId: item.policy_id, routeId };

  const createLink = useCreateRenewalPaymentLink();
  const revokeLink = useRevokeRenewalPaymentLink();
  const rejectReport = useRejectRenewalPaymentReport();
  const confirmPayment = useConfirmRenewalPayment();

  const [showAdd, setShowAdd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [revealed, setRevealed] = useState<string | null>(null);

  const [url, setUrl] = useState('');
  const [amount, setAmount] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [note, setNote] = useState('');
  const [premiumChanged, setPremiumChanged] = useState(false);
  const [notifyCustomer, setNotifyCustomer] = useState(true);

  const [reference, setReference] = useState('');
  const [paidAt, setPaidAt] = useState('');
  const [paidAmount, setPaidAmount] = useState('');
  const [receipt, setReceipt] = useState<File | null>(null);
  const [additional, setAdditional] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  if (item.is_installment_policy) {
    return (
      <Card>
        <CardHeader title="Customer payment link" />
        <p className="text-sm text-gray-500 dark:text-gray-400">
          This policy is paid in instalments; its renewal is the next instalment, not a payment link.
        </p>
      </Card>
    );
  }

  const hasOpenLink = state === 'link_active' || state === 'reported_paid' || (state === 'expired' && block?.status !== 'expired');
  const canAddLink = state !== 'paid' && !item.successor;
  const canConfirmLink = !!block?.link_id && state !== 'paid' && state !== 'no_link';

  const openAdd = () => {
    setUrl('');
    setAmount(block?.amount ?? item.current_premium ?? '');
    setValidUntil('');
    setNote('');
    setPremiumChanged(false);
    setNotifyCustomer(true);
    setFormError(null);
    setShowAdd(true);
  };

  const openConfirm = () => {
    setReference('');
    setPaidAt(localInputValue(new Date()));
    setPaidAmount(block?.amount ?? '');
    setReceipt(null);
    setAdditional(false);
    setFormError(null);
    setShowConfirm(true);
  };

  const submitAdd = (event: FormEvent) => {
    event.preventDefault();
    if (!url.trim().toLowerCase().startsWith('https://')) {
      setFormError('Paste the https:// payment link the insurer gave you.');
      return;
    }
    if (!(Number(amount) > 0)) {
      setFormError('Enter the amount the insurer will charge.');
      return;
    }
    setFormError(null);
    createLink.mutate(
      {
        ...ids,
        url: url.trim(),
        amount,
        expires_at: toIso(validUntil),
        note,
        premium_changed: premiumChanged,
        notify_customer: notifyCustomer,
      },
      { onSuccess: () => setShowAdd(false) }
    );
  };

  const submitConfirm = (event: FormEvent) => {
    event.preventDefault();
    if (!block?.link_id) return;
    const paidIso = toIso(paidAt);
    if (!reference.trim() || !paidIso) {
      setFormError('Enter the insurer reference and when the customer paid.');
      return;
    }
    if (receipt && receipt.size > RECEIPT_MAX_BYTES) {
      setFormError('The receipt file is larger than 5 MB.');
      return;
    }
    setFormError(null);
    confirmPayment.mutate(
      {
        ...ids,
        linkId: block.link_id,
        insurer_reference: reference.trim(),
        paid_at: paidIso,
        amount: paidAmount || undefined,
        receipt_file: receipt,
        additional_payment: additional,
      },
      { onSuccess: () => setShowConfirm(false) }
    );
  };

  const reveal = async () => {
    if (!block?.link_id) return;
    try {
      const data = await renewalService.revealPaymentLink(block.link_id);
      setRevealed(data.url);
    } catch {
      setRevealed(null);
    }
  };

  const amountDiffers =
    !!paidAmount && !!block?.amount && Number(paidAmount) > 0 && Number(paidAmount) !== Number(block.amount);

  return (
    <Card>
      <CardHeader
        title="Customer payment link"
        subtitle="The insurer's own payment page, passed to the customer"
        action={
          label ? (
            <Badge variant={getStatusBadgeVariant(label.badge)} size="sm">
              {label.deskLabel}
            </Badge>
          ) : undefined
        }
      />
      <div className="space-y-3 text-sm">
        {state === 'no_link' && (
          <p className="text-gray-500 dark:text-gray-400">
            No link yet. Generate the renewal payment link on the insurer&apos;s site and add it here; the customer
            then sees Renew now.
          </p>
        )}
        {block && state !== 'no_link' && (
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <dt className="text-gray-500 dark:text-gray-400">Amount</dt>
              <dd className="font-medium text-gray-900 dark:text-gray-100">
                {block.amount ? formatCurrency(block.amount) : '—'}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500 dark:text-gray-400">Valid until</dt>
              <dd className="font-medium text-gray-900 dark:text-gray-100">
                {block.expires_at ? formatDateTime(block.expires_at) : 'Not stated'}
              </dd>
            </div>
            {block.url_host && (
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Payment site</dt>
                <dd className="font-medium text-gray-900 dark:text-gray-100 break-all">
                  {block.url_host}{' '}
                  {block.host_verified === false && (
                    <span className="text-xs text-amber-700 dark:text-amber-400">(not on the insurer&apos;s list)</span>
                  )}
                </dd>
              </div>
            )}
            <div>
              <dt className="text-gray-500 dark:text-gray-400">Opened by customer</dt>
              <dd className="font-medium text-gray-900 dark:text-gray-100">
                {block.open_count ? `${block.open_count} time(s), last ${formatDateTime(block.last_opened_at)}` : 'Not yet'}
              </dd>
            </div>
          </dl>
        )}
        {state === 'reported_paid' && block?.reported_at && (
          <p className="rounded-md bg-amber-50 p-3 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
            Customer says they paid on {formatDateTime(block.reported_at)}. Check with the insurer and confirm.
          </p>
        )}
        {state === 'paid' && block?.receipt && (
          <p className="rounded-md bg-green-50 p-3 text-green-800 dark:bg-green-900/30 dark:text-green-200">
            Paid {block.receipt.paid_at ? formatDateTime(block.receipt.paid_at) : ''} — receipt {block.receipt.number}
            {block.receipt.insurer_reference ? `, insurer reference ${block.receipt.insurer_reference}` : ''}
            {block.receipt.is_acknowledgement ? ' (platform acknowledgement)' : ''}.
          </p>
        )}
        {block?.status_reason && state !== 'paid' && (
          <p className="text-xs text-gray-500 dark:text-gray-400">Note: {block.status_reason}</p>
        )}
        {revealed && (
          <p className="break-all rounded-md bg-gray-50 p-2 text-xs text-gray-700 dark:bg-slate-800 dark:text-gray-300">
            {revealed}
          </p>
        )}
        {canEdit && (
          <div className="flex flex-wrap gap-2 pt-1">
            {canAddLink && (
              <Button size="sm" variant={state === 'no_link' ? 'primary' : 'outline'} onClick={openAdd}>
                {state === 'no_link' ? 'Add payment link' : 'Replace link'}
              </Button>
            )}
            {canConfirm && canConfirmLink && (
              <Button size="sm" onClick={openConfirm}>
                Confirm payment
              </Button>
            )}
            {state === 'reported_paid' && block?.link_id && (
              <Button
                size="sm"
                variant="outline"
                isLoading={rejectReport.isPending}
                onClick={() => rejectReport.mutate({ ...ids, linkId: block.link_id!, reason: 'Payment not found' })}
              >
                Not received
              </Button>
            )}
            {hasOpenLink && block?.link_id && (
              <>
                <Button size="sm" variant="outline" onClick={() => void reveal()}>
                  Show link
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  isLoading={revokeLink.isPending}
                  onClick={() => revokeLink.mutate({ ...ids, linkId: block.link_id!, reason: '' })}
                >
                  Revoke
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      <Modal isOpen={showAdd} onClose={() => setShowAdd(false)} title="Customer payment link">
        <form onSubmit={submitAdd} className="space-y-4" aria-label="Add payment link">
          <Input
            id="rpl-url"
            label="Insurer's payment link"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://"
            required
          />
          <Input
            id="rpl-amount"
            label="Amount to be paid"
            type="number"
            min="0"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
          />
          <Input
            id="rpl-valid-until"
            label="Valid until (optional)"
            type="datetime-local"
            value={validUntil}
            onChange={(e) => setValidUntil(e.target.value)}
            helperText="Leave blank if the insurer did not say; the link is treated as valid for 7 days."
          />
          <Input id="rpl-note" label="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input type="checkbox" checked={premiumChanged} onChange={(e) => setPremiumChanged(e.target.checked)} />
            The renewal premium changed (amount differs from last year by more than 25%)
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input type="checkbox" checked={notifyCustomer} onChange={(e) => setNotifyCustomer(e.target.checked)} />
            Notify customer now
          </label>
          {formError && <p className="text-sm text-red-600 dark:text-red-400">{formError}</p>}
          <ModalFooter>
            <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>
              Cancel
            </Button>
            <Button type="submit" isLoading={createLink.isPending}>
              Save and notify customer
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      <Modal isOpen={showConfirm} onClose={() => setShowConfirm(false)} title="Confirm renewal payment">
        <form onSubmit={submitConfirm} className="space-y-4" aria-label="Confirm payment">
          <Input
            id="rpc-reference"
            label="Insurer reference"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            helperText="The insurer's transaction or receipt number."
            required
          />
          <Input
            id="rpc-paid-at"
            label="Paid on"
            type="datetime-local"
            value={paidAt}
            onChange={(e) => setPaidAt(e.target.value)}
            required
          />
          <Input
            id="rpc-amount"
            label="Amount paid"
            type="number"
            min="0"
            step="0.01"
            value={paidAmount}
            onChange={(e) => setPaidAmount(e.target.value)}
            helperText={amountDiffers ? 'Differs from the link amount; check before confirming.' : undefined}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" htmlFor="receipt-file">
              Insurer&apos;s receipt (PDF, JPG or PNG, up to 5 MB)
            </label>
            <input
              id="receipt-file"
              type="file"
              accept={RECEIPT_ACCEPT}
              onChange={(e) => setReceipt(e.target.files?.[0] ?? null)}
              className="block w-full text-sm"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Without a file the customer gets a payment acknowledgement, labelled as not the insurer&apos;s receipt.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input type="checkbox" checked={additional} onChange={(e) => setAdditional(e.target.checked)} />
            Additional payment (the customer really paid twice)
          </label>
          {formError && <p className="text-sm text-red-600 dark:text-red-400">{formError}</p>}
          <ModalFooter>
            <Button type="button" variant="outline" onClick={() => setShowConfirm(false)}>
              Cancel
            </Button>
            <Button type="submit" isLoading={confirmPayment.isPending}>
              Confirm payment
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </Card>
  );
}
